/* eslint-disable complexity */
/* eslint-disable no-use-before-define */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { graphic } from 'echarts/core';
import type { SeriesModel } from 'echarts/lib/echarts.all';
import { type BoundingRect } from '../../../model/util/coord';
import { Line } from 'zrender';

/** 旗子标记对应的信息 */
export interface FlagLayoutInfo {
  /** 旗子标记的方框对应的zrender元素 */
  rect: graphic.Rect;
  /** 旗子标记的文本对应的zrender元素 */
  label: graphic.Text;
  /** 旗子标记的旗杆对应的zrender元素 */
  line: graphic.Line;
  /** 旗子标记的整体容器对应的zrender元素 */
  group: graphic.Group;
  /** 旗子标记的圆点对应的zrender元素 */
  circle: graphic.Circle;
  /** 旗子标记的文本和方框所在容器对应的zrender元素 */
  insideGroup: graphic.Group;
  /** 旗子标记的文本和方框对应的包围矩形的信息 */
  boundRect: BoundingRect;
  /** 旗子标记的显示优先级 */
  weight: number;
}
export type FlagInfo = Pick<
  FlagLayoutInfo,
  'rect' | 'insideGroup' | 'line' | 'boundRect' | 'group' | 'circle' | 'weight'
> & {
  shifted?: boolean;
};

type FlagGroup = graphic.Group & {
  flag_group_inside?: graphic.Group;
  flag_rect?: graphic.Rect;
  flag_text?: graphic.Text;
  flag_line?: graphic.Line;
  flag_circle?: graphic.Circle;
};

/**
 * 用于拆分代码的内部方法
 * @remarks 主要功能是根据旗子的group元素，解构出旗子的各项信息，并添加到旗子信息列表中
 */
function addFlagInfo(out: FlagInfo[], flagItem: FlagGroup, flagData) {
  const {
    flag_rect: rect,
    flag_line: line,
    flag_group_inside: insideGroup,
    flag_circle: circle
  } = flagItem;
  const boundRect: BoundingRect = {
    x: insideGroup.x,
    y: insideGroup.y,
    height: rect.shape.height,
    width: rect.shape.width
  };
  out.push({
    rect,
    line,
    circle,
    insideGroup,
    boundRect,
    group: flagItem,
    weight: flagData.weight,
    name: flagData.value[0]
  });
}
/**
 * 预处理旗子信息的列表
 */
export function prepareFlagInfoList(chartModel: SeriesModel) {
  // 定义旗子各个组成部分的ID的标志性字符信息
  const flagPartNames = ['flag_group_inside', 'flag_rect', 'flag_text', 'flag_line', 'flag_circle'];
  // 用于存储处理完成的旗子信息的列表
  const flagInfoList: FlagInfo[] = [];
  // 遍历标记系列视图View下的每一个旗子
  const seriesData = chartModel.getData();
  seriesData.eachItemGraphicEl((flagItem: FlagGroup, index) => {
    // 对于每一个旗子的group元素，遍历其全部后代元素（包含所有的叶子节点和枝干节点）
    flagItem.traverse(el => {
      // 如果当前元素已经被标记为忽略，则跳过
      if (el.ignore) {
        return;
      }
      // 对于当前元素，依次判别是旗子的哪一个组成部分，如果判定出是具体某部分，则将当前元素挂载到旗子的最外层group上
      flagPartNames.forEach(name => {
        if ((el.extra.dvid as string).indexOf(name) !== -1) {
          flagItem[name] = el;
        }
      });
    });

    // 如果当前旗子没有忽略，则将当前旗子的信息加入到旗子信息列表
    if (!flagItem.ignore) {
      addFlagInfo(flagInfoList, flagItem, seriesData.getRawDataItem(index));
    }
  });

  return flagInfoList;
}

/**
 * 判断两个区间是否相交
 * @param param0
 * @param param1
 * @returns
 */
function isSegmentIntersect([a, b], [c, d]) {
  return d > a && b > c;
}

function useMap<K, V>() {
  const map = new Map<K, V>();
  const get = (key: K) => map.get(key);
  const set = (key: K, value: V) => map.set(key, value);
  return [get, set] as const;
}

type Interval = [number, number];
class SegmentSplitter {
  private segments: Interval[] = [];

  private middle: number;

  constructor(start: number, end: number) {
    this.segments.push([start, end]);
    this.middle = (start + end) / 2;
  }

  addInterval(i: number, j: number): void {
    if (i >= j) {
      return;
    }
    const newSegments: Interval[] = [];
    this.segments.forEach(([start, end]) => {
      if (!isSegmentIntersect([start, end], [i, j])) {
        newSegments.push([start, end]);
      } else {
        if (start < i) {
          newSegments.push([start, i]);
        }
        if (j < end) {
          newSegments.push([j, end]);
        }
      }
    });
    this.segments = newSegments;
  }

  getSegmentByLength(start: number, end: number, excludePoint: number, direction: -1 | 1): [number, number] | null {
    const length = end - start;
    function getSegment(segStart: number, segEnd: number, dir: -1 | 1): [number, number] | null {
      let resSegment: [number, number] | null = null;
      // 当前片段可包含此区间
      if (segEnd - segStart >= length) {
        // 当前片段完全包含区间[()], 则不需要移动
        if (segStart <= start && end <= segEnd) {
          resSegment = [start, end];
        } else {
          // 如果移动方向为从大到小
          if (dir < 0) {
            // 只有当前片段在区间左边：[(]) 或者 []()时，存在合适片段
            resSegment = segStart <= start ? [segEnd - length, segEnd] : null;
          } else {
            // 否则，只有当前片段在区间右边：([)] 或者 ()[]，存在合适片段
            resSegment = end <= segEnd ? [segStart, segStart + length] : null;
          }
        }
      }

      if (!resSegment) {
        return null;
      }
      return !(resSegment[0] < excludePoint && excludePoint < resSegment[1]) ? resSegment : null;
      // return resSegment;
    }

    // 搜索初始位置所在的片段
    let startSegmentIndex = this.segments.findIndex(
      // <0 end >0 start TODO
      ([segStart, segEnd]) => direction < 0 ? segStart <= end && end <= segEnd : segStart <= start && start <= segEnd
    );

    if (startSegmentIndex === -1) {
      //  如果初始位置已被占用，则向上寻找最近的片段
      startSegmentIndex = this.segments.findIndex(([segStart, segEnd]) => direction < 0 ? segEnd > end : segStart > start) - 1;

      if (startSegmentIndex === -2) {
        startSegmentIndex = direction > 0 ? this.segments.length - 1 : 0;
      }
    }

    // 优先向指定方向遍历片段
    for (let i = startSegmentIndex; ; i += direction) {
      if (!this.segments[i]) break;
      const [segStart, segEnd] = this.segments[i];
      const segment = getSegment(segStart, segEnd, direction);
      if (segment) {
        return segment;
      }
    }

    // 如果没有结果，则向反方向寻找剩余片段
    for (let i = startSegmentIndex; ; i -= direction) {
      if (!this.segments[i]) break;
      const [segStart, segEnd] = this.segments[i];
      const segment = getSegment(segStart, segEnd, -direction as -1 | 1);
      if (segment) {
        return segment;
      }
    }

    // 没有找到合适片段则返回空
    return null;
  }

  getSegments() {
    return this.segments;
  }
}

function initLayoutInfoHandlers(list: FlagInfo[]) {
  const [y0, setY0] = useMap<FlagInfo, number>();
  const [y, setY] = useMap<FlagInfo, number>();
  const [h, setH] = useMap<FlagInfo, number>();
  const [xx, setXX] = useMap<FlagInfo, [number, number]>();
  list.forEach(labelInfo => {
    const { x, y: labelY, width, height } = labelInfo.boundRect;
    const [labelLeft, labelRight] = width < 0 ? [x + width, x] : [x, x + width];
    setY0(labelInfo, labelY);
    setY(labelInfo, labelY);
    setH(labelInfo, height);
    // [a, b]
    setXX(labelInfo, [labelLeft, labelRight]);
  });
  return { y0, setY0, y, setY, h, setH, xx, setXX };
}

/** 旗子方框的位置调整后，调整旗杆y方向上的终点以及旗子方框的圆角样式
 * @description 旗子方框与旗杆连接处圆角为0
 *  */
function shiftStyles(flagGroupY: number, lineYStart: number, boundHeight: number, rect) {
  const rectWidth = rect.shape.width;
  let lineYEnd = lineYStart;
  // 圆角数组元素依次对应【左上，右上，右下，左下】
  const rectBorderRadius = Array.isArray(rect.shape.r)
    ? rect.shape.r
    : [rect.shape.r, rect.shape.r, rect.shape.r, rect.shape.r];

  if (flagGroupY > lineYStart) {
    // 如果旗子方框上边界的y坐标 > 旗杆起始点y坐标, 即旗子原点在旗子上方, 则正常连到旗子；
    // 旗子方框与旗杆连接处在上方
    lineYEnd = flagGroupY;
    if (rectWidth > 0) {
      rectBorderRadius[0] = 0;
    } else {
      rectBorderRadius[1] = 0;
    }
  } else if (flagGroupY + Math.abs(boundHeight) > lineYStart) {
    // 否则，如果旗子方框上边界的y坐标 < 旗杆起始点y坐标 < 旗子方框下边界的y坐标, 即旗子原点在旗子内部, 则旗杆长度为0
    // 旗子方框与旗杆连接处旗子方框内部，原本的连接处一侧的圆角需要置为0
    lineYEnd = lineYStart;
    if (rectWidth > 0) {
      rectBorderRadius[0] = 0;
      rectBorderRadius[3] = 0;
    } else {
      rectBorderRadius[1] = 0;
      rectBorderRadius[2] = 0;
    }
  } else {
    // 否则，如果旗子方框下边界的y坐标 < 旗杆起始点y坐标 即旗子原点在旗子下, 则旗杆连接到旗子下边界
    // 旗子方框与旗杆连接处在下方
    lineYEnd = flagGroupY + Math.abs(boundHeight);
    if (rectWidth > 0) {
      rectBorderRadius[3] = 0;
    } else {
      rectBorderRadius[2] = 0;
    }
  }

  return { lineYEnd, rectBorderRadius };
}

function doUpdateFlagsLayout(list: FlagInfo[], {y, h}) {
  const len = list.length;
    // 同步布局结果到图形位置上
    for (let i = 0; i < len; i += 1) {
      list[i].insideGroup.attr({
        y: y(list[i])
      });
      const { lineYEnd, rectBorderRadius } = shiftStyles(
        y(list[i]),
        list[i].line.shape.y1,
        h(list[i]),
        list[i].rect
      );
      // 旗杆保持和方框正确连接
      list[i].line.attr({
        shape: {
          y2: lineYEnd
        }
      });
      list[i].rect.attr({
        shape: {
          r: rectBorderRadius
        }
      });
    }
}

// 布局所有标记的位置，不再有重叠的情况
export function shiftLayout(
  list: FlagInfo[],
  minBound: number,
  maxBound: number,
  { y, setY, h, xx }: ReturnType<typeof initLayoutInfoHandlers>,
  flag: boolean
) {
  const padding = 2;
  // 定义无需重新布局的标记数量阈值，一般2个以下的标记不需要重新排布
  const SHIFT_THRESHOLD = 2;
  const len = list.length;
  if (len < SHIFT_THRESHOLD) {
    return;
  }

  // 根据优先级从大到小排序标记，优先级相同则按照水平位置从小到大排序
  list.sort((a, b) => {
    if (a.weight === b.weight) {
      return xx(a)[0] - xx(b)[0];
    }
    return b.weight - a.weight;
  });
  // 初始化已显示的标记列表
  const displayedList: FlagInfo[] = [];
  for (let i = 0; i < len; i += 1) {
    const l0 = list[i];
    const segmentSplitter = new SegmentSplitter(minBound, maxBound);
    displayedList.forEach(existFlag => {
      // 快速判断：如果水平方向不相交，则不可能重叠
      if (!isSegmentIntersect(xx(l0), xx(existFlag))) {
        return;
      }

      if (flag) {
        segmentSplitter.addInterval(y(existFlag), y(existFlag) + h(existFlag) + padding);
      }
    });
    const segment = segmentSplitter.getSegmentByLength(
      y(l0),
      y(l0) + h(l0) + padding,
      l0.circle.y,
      l0.circle.y > (minBound + maxBound) / 2 ? -1 : 1
    );

    if (segment) {
      setY(l0, segment[0]);
      displayedList.push(l0);
    } else {
      // 找不到合适位置 隐藏
      hide(l0);
    }
  }

  function hide(flagItem: FlagInfo) {
    flagItem.insideGroup.attr({ ignore: true });
    flagItem.line.attr({ ignore: true });
    flagItem.circle.attr({ ignore: true });
  }
}

export function shiftLayoutOnY(
  list: Pick<
    FlagLayoutInfo,
    'rect' | 'insideGroup' | 'line' | 'boundRect' | 'group' | 'circle' | 'weight'
  >[],
  topBound: number,
  bottomBound: number,
  needHideOverlap: boolean,
  cb?: typeof shiftLayout
): void {
  const handlers = initLayoutInfoHandlers(list);
  
  if (cb) {
    cb(list, topBound, bottomBound, handlers, !needHideOverlap);
  } else {
    shiftLayout(list, topBound, bottomBound, handlers, !needHideOverlap);
  }
  
  doUpdateFlagsLayout(list, {y: handlers.y, h: handlers.h});
}
